import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Archive,
  BarChart3,
  CalendarClock,
  CalendarX,
  ChevronDown,
  Copy,
  Eye,
  FileText,
  Package,
  Plus,
  Repeat,
  Store,
  Tag,
  Tags,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  usePersistedState,
  useSearchListState,
  type BulkAction,
  type DataTableView,
  type RowAction,
  type SearchPatch,
  type SelectionDescriptor,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  BulkProductsRequest,
  BulkResponse,
  ProductListItem,
  ProductSelection,
  ProductStatus,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ImportExportLinks } from '@/features/imports-exports';
import {
  MAX_SELECTION_PRODUCTS,
  toListSelection,
  useProductFilterDefs,
  withFirstPageTotal,
  type ProductPickerScope,
} from '@/features/resource-pickers';
import { BulkResultDialog } from '@/shared/bulk/bulk-result-dialog';
import { selectionChanged } from '@/shared/bulk/selection-errors';
import { useBulkErrorMessage } from '@/shared/bulk/use-bulk-error-message';
import { useKeptTotal } from '@/shared/bulk/use-kept-total';
import { useSelectionRequest } from '@/shared/bulk/use-selection-request';
import { session } from '@/shared/session/session';

import {
  BulkInputDialog,
  type BulkInputAction,
  type BulkInputResult,
} from '../components/bulk-input-dialog';
import { BulkResultDetail } from '../components/bulk-result-detail';
import { BulkScheduleDialog } from '../components/bulk-schedule-dialog';
import { DuplicateProductDialog } from '../components/duplicate-product-dialog';
import { useProductColumns } from '../components/product-columns';
import { ProductHealthTiles } from '../components/product-health-tiles';
import { useBulkProducts } from '../hooks/use-bulk-products';
import { useProducts } from '../hooks/use-products';

const ROUTE_ID = '/_authed/_active/org/products/' as const;
const ROUTE_PATH = '/org/products/' as const;
const ORG: ProductPickerScope = { kind: 'org' };
const ANALYTICS_KEY = 'nucleus:products:analytics';
const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

/**
 * What a bulk action runs on: the ticked rows of the page, or (after "Select all N") every
 * product the list's filters match, sent as a query selection the server resolves.
 */
type BulkTarget = { ids: string[] } | { selection: ProductSelection };

type BulkRequest = Omit<BulkProductsRequest, 'ids' | 'selection'>;

function ProductsList() {
  const { t } = useTranslation(['products', 'common']);
  const message = useBulkErrorMessage();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { can } = usePermissions();
  const { me } = session.useSession();
  const stores = useMemo(() => me?.stores ?? [], [me?.stores]);
  const bulk = useBulkProducts();
  const selectionRequest = useSelectionRequest();
  const [input, setInput] = useState<BulkInputAction | null>(null);
  const [scheduling, setScheduling] = useState<number | null>(null);
  const [target, setTarget] = useState<BulkTarget | null>(null);
  const [result, setResult] = useState<{
    response: BulkResponse;
    action: BulkProductsRequest['action'];
  } | null>(null);
  const [titles, setTitles] = useState<ReadonlyMap<string, string>>(new Map());
  const [duplicating, setDuplicating] = useState<ProductListItem | null>(null);
  const [analytics, setAnalytics] = usePersistedState<boolean>(ANALYTICS_KEY, true, isBoolean);

  const filters = useProductFilterDefs<ProductListItem>(ORG);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const params = useMemo(() => withFirstPageTotal(list.params), [list.params]);
  const query = useProducts(params);
  // The first page's total, kept while the cursor moves (`cursor: undefined` drops out).
  const counted = useKeptTotal(
    JSON.stringify({ ...list.params, cursor: undefined }),
    query.data,
    query.isPlaceholderData,
  );
  const columns = useProductColumns();

  const views = useMemo<DataTableView[]>(
    () => [
      {
        id: 'attention',
        label: t('views.attention'),
        separatorBefore: true,
        query: { filters: { needsAttention: { op: 'eq', value: true } } },
      },
    ],
    [t],
  );

  /**
   * One bulk call on the target. A query selection the filter no longer matches as counted asks
   * to apply to the new count first; `null` when the user declined.
   */
  const bulkAsync = bulk.mutateAsync;
  const resetBulk = bulk.reset;
  const runRequest = selectionRequest.run;
  const send = useCallback(
    async (request: BulkRequest, on: BulkTarget): Promise<BulkResponse | null> => {
      if ('ids' in on) return bulkAsync({ ...request, ids: on.ids });
      const response = await runRequest(on.selection, (selection) =>
        bulkAsync({ ...request, selection }),
      );
      if (response === null) resetBulk();
      return response;
    },
    [bulkAsync, resetBulk, runRequest],
  );

  const report = useCallback(
    (response: BulkResponse, action: BulkProductsRequest['action'], done: string) => {
      if (response.failed > 0) setResult({ response, action });
      else toast.success(t(done, { count: response.succeeded }));
    },
    [t],
  );

  /**
   * A bulk call without a dialog, or from the input dialog (`fromDialog`), which then shows a
   * whole-call failure itself. A 207 opens the per-row result.
   */
  const run = useCallback(
    async (request: BulkRequest, on: BulkTarget, fromDialog: boolean) => {
      try {
        const response = await send(request, on);
        if (response === null) return;
        setInput(null);
        report(response, request.action, 'toasts.bulkDone');
      } catch (error) {
        if (!fromDialog) toast.error(message(error));
      }
    },
    [message, report, send],
  );

  /** The schedule dialog renders a whole-call failure itself, field errors on their field. */
  const schedule = useCallback(
    async (request: Omit<BulkRequest, 'action'>) => {
      if (!target) return;
      const response = await send({ action: 'schedule', ...request }, target);
      if (response === null) return;
      setScheduling(null);
      report(response, 'schedule', 'toasts.bulkScheduled');
    },
    [report, send, target],
  );

  const listFilters = list.filters;
  const listQ = list.q;
  const bulkActions = useMemo<Array<BulkAction<ProductListItem>>>(() => {
    if (!can('products.write')) return [];
    /** The target of a bulk action and how many products it covers; null when nothing is. */
    const targetOf = (
      selection: SelectionDescriptor,
      rows: readonly ProductListItem[],
    ): { on: BulkTarget; count: number } | null => {
      setTitles(new Map(rows.map((row) => [row.id, row.title])));
      if (selection.type === 'exclude') {
        return {
          on: {
            selection: toListSelection(selection, { filters: listFilters, q: listQ }, 'org'),
          },
          count: selection.total - selection.ids.length,
        };
      }
      return rows.length > 0
        ? { on: { ids: rows.map((row) => row.id) }, count: rows.length }
        : null;
    };
    const setStatus = (
      status: ProductStatus,
      label: string,
      icon: React.ReactNode,
    ): BulkAction<ProductListItem> => ({
      id: `status-${status}`,
      label,
      icon,
      supportsAllMatching: true,
      onAction: async (selection, rows) => {
        const picked = targetOf(selection, rows);
        if (picked) await run({ action: 'set_status', status }, picked.on, false);
      },
    });
    const ask = (
      kind: BulkInputAction['kind'],
      label: string,
      icon: React.ReactNode,
      variant?: 'destructive',
    ): BulkAction<ProductListItem> => ({
      id: kind,
      label,
      icon,
      supportsAllMatching: true,
      ...(variant ? { variant } : {}),
      onAction: (selection, rows) => {
        const picked = targetOf(selection, rows);
        if (!picked) return;
        bulk.reset();
        setTarget(picked.on);
        setInput({ kind, count: picked.count });
      },
    });
    return [
      { ...ask('publish', t('bulk.publish'), <Store aria-hidden />), promoted: true },
      { ...setStatus('active', t('bulk.status.active'), <Eye aria-hidden />), promoted: true },
      setStatus('draft', t('bulk.status.draft'), <FileText aria-hidden />),
      setStatus('archived', t('bulk.status.archived'), <Archive aria-hidden />),
      {
        id: 'schedule',
        label: t('bulk.schedule.action'),
        icon: <CalendarClock aria-hidden />,
        supportsAllMatching: true,
        onAction: (selection, rows) => {
          const picked = targetOf(selection, rows);
          if (!picked) return;
          bulk.reset();
          setTarget(picked.on);
          setScheduling(picked.count);
        },
      },
      ask('clear_schedule', t('bulk.clearSchedule'), <CalendarX aria-hidden />),
      {
        // The new rotation form holds a list of products, not a filter: ticked rows only. For
        // every matching product, create the rotation and use "Add products" on its pool.
        id: 'rotation',
        label: t('bulk.createRotation'),
        icon: <Repeat aria-hidden />,
        onAction: (_selection, rows) => {
          if (rows.length === 0) return;
          void navigate({
            to: '/org/schedules/new',
            search: { productIds: rows.map((row) => row.id).join(',') },
          });
        },
      },
      ask('add_tags', t('bulk.addTags'), <Tag aria-hidden />),
      ask('remove_tags', t('bulk.removeTags'), <Tags aria-hidden />),
      ask('delete', t('bulk.delete'), <Trash2 aria-hidden />, 'destructive'),
    ];
  }, [bulk, can, listFilters, listQ, navigate, run, t]);

  const rowActions = useMemo<Array<RowAction<ProductListItem>>>(() => {
    if (!can('products.write')) return [];
    return [
      {
        id: 'duplicate',
        label: t('duplicate.action'),
        icon: <Copy aria-hidden />,
        onAction: setDuplicating,
      },
      {
        id: 'publish',
        label: t('list.publishRow'),
        icon: <Store aria-hidden />,
        // The backend refuses publishing anything else (see bulk.publishDescription).
        disabled: (row) => row.status !== 'active',
        onAction: (row) => {
          bulk.reset();
          setTitles(new Map([[row.id, row.title]]));
          setTarget({ ids: [row.id] });
          setInput({ kind: 'publish', count: 1 });
        },
      },
      {
        id: 'archive',
        label: t('bulk.status.archived'),
        icon: <Archive aria-hidden />,
        separatorBefore: true,
        hidden: (row) => row.status === 'archived',
        onAction: async (row) => {
          setTitles(new Map([[row.id, row.title]]));
          await run({ action: 'set_status', status: 'archived' }, { ids: [row.id] }, false);
        },
      },
    ];
  }, [bulk, can, run, t]);

  const submitInput = (value: BulkInputResult) => {
    if (!input || !target) return;
    const request: BulkRequest =
      value.kind === 'publish' || value.kind === 'clear_schedule'
        ? { action: value.kind, storeIds: value.storeIds }
        : value.kind === 'delete'
          ? { action: 'delete' }
          : { action: value.kind, tags: value.tags };
    void run(request, target, true);
  };

  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    total: counted.total,
    totalCapped: counted.totalCapped,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'products',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('listTitle')}
        description={isEmptyList ? undefined : t('listDescription')}
        actions={
          <>
            {isEmptyList ? null : (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  aria-pressed={analytics}
                  aria-label={t(analytics ? 'list.hideAnalytics' : 'list.showAnalytics')}
                  title={t(analytics ? 'list.hideAnalytics' : 'list.showAnalytics')}
                  className={analytics ? 'bg-accent text-accent-foreground' : undefined}
                  onClick={() => setAnalytics(!analytics)}
                >
                  <BarChart3 aria-hidden />
                </Button>
                {stores[0] ? (
                  <ImportExportLinks storeId={stores[0].id} kind="products_csv" />
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" aria-label={t('list.moreActionsLabel')}>
                      {t('list.moreActions')}
                      <ChevronDown aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/org/schedules">{t('list.schedules')}</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/org/catalog-setup">{t('list.catalogSetup')}</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            <Can permission="products.write">
              <Button asChild data-tour="catalog.products.add-button">
                <Link to="/org/products/new">
                  <Plus aria-hidden />
                  {t('add')}
                </Link>
              </Button>
            </Can>
          </>
        }
      />
      {analytics && !isEmptyList ? (
        <ProductHealthTiles
          filters={list.filters}
          onFiltersChange={(next) => list.update({ filters: next })}
          reportStoreId={stores[0]?.id}
        />
      ) : null}
      {isEmptyList ? (
        <EmptyState
          tour="catalog.products.empty"
          tourLink
          icon={Package}
          title={t('empty.title')}
          description={t('empty.description')}
          action={
            <Can permission="products.write">
              <Button asChild>
                <Link to="/org/products/new">{t('empty.action')}</Link>
              </Button>
            </Can>
          }
        />
      ) : (
        <DataTable
          table={table}
          search={{
            placeholder: t('searchPlaceholder'),
            label: t('search'),
            help: t('searchHint'),
          }}
          pillFilter="status"
          views={views}
          footerTotal
          loading={query.isPending}
          fetching={query.isFetching || bulk.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({ to: '/org/products/$productId', params: { productId: row.id } })
          }
          getRowLabel={(row) => row.title}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          bulkActions={bulkActions}
          rowActions={rowActions}
          stickyLastColumn
          allMatchingMax={MAX_SELECTION_PRODUCTS}
          tour={{
            table: 'catalog.products.table',
            search: 'catalog.products.search',
            filters: 'catalog.products.filters',
          }}
        />
      )}
      <BulkInputDialog
        action={input}
        stores={stores}
        pending={bulk.isPending}
        error={input && !selectionChanged(bulk.error) ? bulk.error : null}
        onSubmit={submitInput}
        onClose={() => setInput(null)}
      />
      <BulkScheduleDialog
        count={scheduling}
        stores={stores}
        pending={bulk.isPending}
        onSubmit={schedule}
        onClose={() => setScheduling(null)}
      />
      <BulkResultDialog
        result={result?.response ?? null}
        titles={titles}
        fallbackTitle={(id) => t('bulk.result.unknownProduct', { id })}
        lookupProductTitles
        description={t('bulk.result.description')}
        detail={(item) =>
          result ? (
            <BulkResultDetail
              item={item}
              action={result.action}
              stores={stores}
              onNavigate={() => setResult(null)}
            />
          ) : null
        }
        onClose={() => setResult(null)}
      />
      {duplicating ? (
        <DuplicateProductDialog
          product={duplicating}
          open
          onOpenChange={(open) => {
            if (!open) setDuplicating(null);
          }}
        />
      ) : null}
      {selectionRequest.dialog}
    </PageContent>
  );
}

export function ProductsListPage() {
  return (
    <FeatureErrorBoundary name="products.list">
      <ProductsList />
    </FeatureErrorBoundary>
  );
}
